import { useEffect, useRef } from "react";
import { useAccount } from "wagmi";
import { useSelector } from "react-redux";

import { GAME_STATUS } from "../constants/game";
import { reselectCommunityCards } from "../routes/reselectors";

import {
  beepAudio,
  chipsAudio,
  newPlayerAudio,
  cardPlaceAudio,
  winnerAudio,
  lostAudio,
  foldAudio,
} from "../sound";
import { type RootState } from "../store";
import useSound from "./useSound";

// the game soundd should only play once, no matter whether user pause the game or navigate to the setting pages
export default function useGameSounds() {
  const prevCardLenRef = useRef(0);
  const prevPlayerLen = useRef(0);

  const { address } = useAccount();
  const lowercaseAddr = address?.toLowerCase();
  const { playSound } = useSound();

  const {
    addtionalResp: { userToAct, currentStage, foldedUsers } = {},
    userInfo: { users, bigBlind, smallBlind } = {},
    gameInfo: { status } = {},
    addtionalUserInfos,
  } = useSelector((state: RootState) => state.game);
  const { communityCards } = useSelector(reselectCommunityCards);

  const myWinAmount = lowercaseAddr
    ? //   @ts-ignore
      addtionalUserInfos?.[lowercaseAddr]?.winAmount
    : undefined;

  // play beep sound when it's current player's turn
  useEffect(() => {
    if (lowercaseAddr && userToAct && lowercaseAddr === userToAct) {
      playSound(beepAudio);
    }
  }, [lowercaseAddr, userToAct]);

  // play chips if the current player is sb / bb
  useEffect(() => {
    if (
      lowercaseAddr &&
      (lowercaseAddr === smallBlind || lowercaseAddr === bigBlind) &&
      status === GAME_STATUS.PLAYING
    ) {
      playSound(chipsAudio);
    }
  }, [lowercaseAddr, smallBlind, bigBlind, status]);

  // play audio when new player sit on the table
  const usersLen = Object.keys(users ?? {}).length;
  useEffect(() => {
    if (lowercaseAddr && usersLen && usersLen > prevPlayerLen.current) {
      playSound(newPlayerAudio);
      prevPlayerLen.current = usersLen;
    }
  }, [lowercaseAddr, usersLen]);

  // play card audio when community card exist
  useEffect(() => {
    if (!communityCards) {
      prevCardLenRef.current = 0;
      return;
    }
    const playSingleSound = async () => {
      try {
        cardPlaceAudio.currentTime = 0;
        await playSound(cardPlaceAudio);
      } catch (e) {
        console.error(e);
      }
    };
    const playMultiSound = async (diff: number) => {
      for (let i = 0; i < diff; i++) {
        setTimeout(() => {
          playSingleSound();
        }, i * 200);
      }
    };
    if (communityCards.length > prevCardLenRef.current) {
      const diff = communityCards.length - prevCardLenRef.current;
      if (diff === 1) {
        playSingleSound();
      } else {
        playMultiSound(diff);
      }
      prevCardLenRef.current = communityCards!.length;
    } else if (communityCards.length < prevCardLenRef.current) {
      prevCardLenRef.current = communityCards.length;
    }
  }, [lowercaseAddr, communityCards?.length]);

  // play win/lost audio
  useEffect(() => {
    const playWinLostAudio = async () => {
      try {
        if (myWinAmount > 0) {
          await playSound(winnerAudio);
        } else {
          await playSound(lostAudio);
        }
      } catch (e) {
        console.error(e);
      }
    };
    if (lowercaseAddr && currentStage === GAME_STATUS.ENDED) {
      playWinLostAudio();
    }
  }, [lowercaseAddr, myWinAmount, currentStage]);

  // fold audio
  useEffect(() => {
    if (foldedUsers?.length) {
      playSound(foldAudio);
    }
  }, [foldedUsers?.length]);
}
